// Cache de buscas para evitar requisições repetidas

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class SearchCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private maxSize: number = 100;

  set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
    // Limpar entradas expiradas
    this.cleanup();

    // Se atingiu o limite, remover a entrada mais antiga
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Verificar se expirou
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  clear(): void {
    this.cache.clear();
  }

  private cleanup(): void {
    const now = Date.now();
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      const entry = this.cache.get(key);
      if (entry && now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    });
  }

  getStats(): { size: number; maxSize: number; usage: string } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      usage: `${Math.round((this.cache.size / this.maxSize) * 100)}%`,
    };
  }
}

export const searchCache = new SearchCache();

export function createSearchCacheKey(params: {
  query?: string;
  filters?: string[];
  sort?: string[];
  limit?: number;
  offset?: number;
}): string {
  return JSON.stringify(params);
}

export async function cachedSearch<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
  ttl: number = 5 * 60 * 1000
): Promise<T> {
  // Verificar cache
  const cached = searchCache.get<T>(cacheKey);
  if (cached) {
    console.log('📦 Cache hit:', cacheKey.substring(0, 50));
    return cached;
  }

  // Buscar dados
  console.log('🔍 Cache miss, fetching:', cacheKey.substring(0, 50));
  const data = await fetchFn();

  // Salvar no cache
  searchCache.set(cacheKey, data, ttl);

  return data;
}
